import { useEffect, useState } from "react";
import { api } from "../api";
import type { Settings } from "../../server/types";

type SettingsForm = Settings & Record<string, string | number | boolean>;

export default function SettingsPage() {
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try { setForm((await api.getSettings()).settings as SettingsForm); }
    catch (e) { setErr((e as Error).message); }
  }
  useEffect(() => { void refresh(); }, []);

  function update<K extends keyof Settings>(k: K, v: Settings[K]) {
    if (!form) return;
    setForm({ ...form, [k]: v as SettingsForm[K] });
    setSaved(false);
  }

  async function save() {
    if (!form) return;
    setBusy(true); setErr(null);
    try {
      const patch: Partial<Settings> = { ...form };
      // Don't send the redacted marker — server skips it anyway, but be explicit.
      await api.updateSettings(patch);
      setSaved(true);
      void refresh();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  if (!form) return <div className="muted">Loading…</div>;

  return (
    <>
      <h2>Settings</h2>
      {err && <div className="error-box" style={{ marginBottom: 16 }}>{err}</div>}
      {saved && <div className="info-box" style={{ marginBottom: 16 }}>Saved.</div>}

      <div className="card">
        <h3>Replica</h3>
        <div className="grid-2">
          <div><label>Host (private VLAN IP)</label>
            <input value={form.replica_host} onChange={(e) => update("replica_host", e.target.value)} placeholder="10.0.0.5" /></div>
          <div><label>Port</label>
            <input type="number" value={form.replica_port} onChange={(e) => update("replica_port", Number(e.target.value))} /></div>
          <div><label>User</label>
            <input value={form.replica_user} onChange={(e) => update("replica_user", e.target.value)} placeholder="backup_ro" /></div>
          <div><label>Password <span className="muted">(leave as ******** to keep current)</span></label>
            <input type="password" value={form.replica_password}
              onChange={(e) => update("replica_password", e.target.value)} /></div>
          <div><label>Database</label>
            <input value={form.replica_database} onChange={(e) => update("replica_database", e.target.value)} /></div>
          <div><label>SSL mode</label>
            <select value={form.replica_sslmode}
              onChange={(e) => update("replica_sslmode", e.target.value as Settings["replica_sslmode"])}>
              <option value="disable">disable</option>
              <option value="prefer">prefer</option>
              <option value="require">require</option>
            </select></div>
          <div><label>Lag threshold (seconds)</label>
            <input type="number" value={form.lag_threshold_seconds}
              onChange={(e) => update("lag_threshold_seconds", Number(e.target.value))} /></div>
        </div>
      </div>

      <div className="card">
        <h3>Retention (GFS)</h3>
        <div className="grid-3">
          <div><label>Daily — keep N</label>
            <input type="number" value={form.retention_daily}
              onChange={(e) => update("retention_daily", Number(e.target.value))} /></div>
          <div><label>Weekly — keep N</label>
            <input type="number" value={form.retention_weekly}
              onChange={(e) => update("retention_weekly", Number(e.target.value))} /></div>
          <div><label>Monthly — keep N</label>
            <input type="number" value={form.retention_monthly}
              onChange={(e) => update("retention_monthly", Number(e.target.value))} /></div>
        </div>
      </div>

      <div className="card">
        <h3>Webhook alert (optional)</h3>
        <p className="muted">POSTed as JSON on backup failure. Slack-compatible (the body has a top-level <code>text</code> field).</p>
        <div><label>Webhook URL</label>
          <input value={form.alert_webhook_url} onChange={(e) => update("alert_webhook_url", e.target.value)}
            placeholder="https://hooks.slack.com/... or any HTTPS endpoint" /></div>
      </div>

      <div className="card">
        <h3>Email alert (optional)</h3>
        <p className="muted">SMTP-based. Leave Email-to blank to disable email alerts.</p>
        <div className="grid-2">
          <div><label>Email to (recipient)</label>
            <input value={form.alert_email_to} onChange={(e) => update("alert_email_to", e.target.value)}
              placeholder="ops@example.com" /></div>
          <div><label>From address</label>
            <input value={form.smtp_from} onChange={(e) => update("smtp_from", e.target.value)}
              placeholder="pg-backup@example.com" /></div>
          <div><label>SMTP host</label>
            <input value={form.smtp_host} onChange={(e) => update("smtp_host", e.target.value)}
              placeholder="smtp.example.com" /></div>
          <div><label>SMTP port</label>
            <input type="number" value={form.smtp_port}
              onChange={(e) => update("smtp_port", Number(e.target.value))}
              placeholder="587" /></div>
          <div><label>SMTP user (optional)</label>
            <input value={form.smtp_user} onChange={(e) => update("smtp_user", e.target.value)} /></div>
          <div><label>SMTP password <span className="muted">(leave as ******** to keep current)</span></label>
            <input type="password" value={form.smtp_password}
              onChange={(e) => update("smtp_password", e.target.value)} /></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24 }}>
            <input type="checkbox" style={{ width: "auto" }}
              checked={form.smtp_secure}
              onChange={(e) => update("smtp_secure", e.target.checked)} />
            <span>Implicit TLS (port 465). Uncheck for STARTTLS / port 587.</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Other</h3>
        <div><label>pg_dump path</label>
          <input value={form.pg_dump_path} onChange={(e) => update("pg_dump_path", e.target.value)} /></div>
      </div>

      <div className="row">
        <button className="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save settings"}</button>
        <button onClick={() => void refresh()} disabled={busy}>Discard changes</button>
      </div>
    </>
  );
}
